import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useSupabaseQuery } from '../../hooks/useSupabase';
import { Save, ChevronRight, AlertCircle, Target, Brain, Settings } from 'lucide-react';
import { useAdminLayoutContext } from '../../components/admin/AdminLayout';

interface SymptomGroup {
  id: string;
  name_no: string;
  order_index: number;
}

interface Symptom {
  id: string;
  group_id: string;
  name_no: string;
  help_text?: string;
  risk_tag?: string;
  order_index?: number;
  group?: SymptomGroup;
}

interface Approach {
  id: string;
  number: number;
  name_no: string;
  description?: string;
}

interface ApproachWithWeight {
  approach: Approach;
  weight: number;
  hasRule: boolean;
}

export function InfluenceRules() {
  const { profile, hasRole } = useAuth();
  const { setHeaderAction } = useAdminLayoutContext();
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectedSymptomId, setSelectedSymptomId] = useState<string | null>(null);
  const [weights, setWeights] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const canEdit = hasRole(['superadmin', 'org_admin', 'editor']);

  // Fetch data
  const { data: symptomGroups } = useSupabaseQuery<SymptomGroup[]>(
    'symptom_groups',
    {
      filter: (q: any) => q.eq('status', 'published'),
      order: { column: 'order_index', ascending: true },
    }
  );

  const { data: symptoms } = useSupabaseQuery<Symptom[]>(
    'symptoms',
    {
      select: '*, group:symptom_groups(*)',
      filter: (q: any) => {
        let query = q.eq('status', 'published');
        if (selectedGroupId) query = query.eq('group_id', selectedGroupId);
        return query;
      },
      order: { column: 'order_index', ascending: true },
    }
  );

  const { data: approaches } = useSupabaseQuery<Approach[]>(
    'approaches',
    {
      filter: (q: any) => q.eq('status', 'published'),
      order: { column: 'number', ascending: true },
    }
  );

  const { data: influenceRules, refetch: refetchRules } = useSupabaseQuery<any[]>(
    'influence_rules',
    {
      select: '*, approach:approaches(*)',
      filter: (q: any) => q.eq('symptom_id', selectedSymptomId || ''),
      enabled: !!selectedSymptomId,
    }
  );

  // Build approach weights from rules
  const approachWeights: ApproachWithWeight[] | undefined = React.useMemo(() => {
    if (!approaches || !selectedSymptomId) return undefined;
    return approaches.map(approach => {
      const rule = influenceRules?.find((r: any) => r.approach_id === approach.id);
      return {
        approach,
        weight: rule?.weight || 0,
        hasRule: !!rule,
      };
    });
  }, [approaches, influenceRules, selectedSymptomId]);

  // Initialize weights when symptom is selected
  React.useEffect(() => {
    if (approachWeights) {
      const newWeights: Record<string, number> = {};
      approachWeights.forEach(aw => {
        newWeights[aw.approach.id] = aw.weight;
      });
      setWeights(newWeights);
    }
  }, [approachWeights]);

  const handleSymptomSelect = (symptomId: string) => {
    setSelectedSymptomId(symptomId);
    setMessage(null);
  };

  const handleWeightChange = (approachId: string, value: number) => {
    setWeights(prev => ({
      ...prev,
      [approachId]: value,
    }));
  };

  const handleSave = async () => {
    if (!selectedSymptomId) {
      setMessage({ type: 'error', text: 'Velg et symptom først' });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      const rules = Object.entries(weights)
        .map(([approachId, weight]) => ({
          symptom_id: selectedSymptomId,
          approach_id: approachId,
          weight: weight,
        }));

      // Upsert all rules for this symptom
      const { error } = await supabase
        .from('influence_rules')
        .upsert(rules, { onConflict: 'symptom_id,approach_id' });

      if (error) throw error;

      await refetchRules();
      setMessage({ type: 'success', text: `Lagret ${rules.length} vektinger` });
    } catch (error: any) {
      console.error('Error saving influence rules:', error);
      setMessage({ type: 'error', text: error.message || 'Kunne ikke lagre vektinger' });
    } finally {
      setSaving(false);
    }
  };

  // Move save button to admin header
  useEffect(() => {
    if (selectedSymptomId && canEdit) {
      setHeaderAction(
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 text-sm font-medium"
        >
          <Save className="h-4 w-4" />
          {saving ? 'Lagrer...' : 'Lagre vektinger'}
        </button>
      );
    } else {
      setHeaderAction(null);
    }
    return () => setHeaderAction(null);
  }, [selectedSymptomId, canEdit, saving, weights]);

  const selectedSymptom = symptoms?.find(s => s.id === selectedSymptomId);
  const filteredSymptoms = selectedGroupId
    ? symptoms?.filter(s => s.group_id === selectedGroupId)
    : symptoms;

  if (!symptomGroups || !symptoms || !approaches) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600 dark:text-gray-400">Laster influence rules...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Påvirkningsregler</h2>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Sett vekting (0-100%) for hvordan symptomer påvirker anbefalte moduler
        </p>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg ${
          message.type === 'success'
            ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
            : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
        }`}>
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            {message.text}
          </div>
        </div>
      )}

      {/* 3-column layout */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12 divide-x divide-gray-200 dark:divide-gray-700">

          {/* Symptom Groups Column */}
          <div className="lg:col-span-3 p-4">
            <h3 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Brain className="h-5 w-5 text-blue-600" />
              Symptomgrupper
            </h3>
            <div className="space-y-2 max-h-[800px] overflow-y-auto">
              {symptomGroups.map((group) => {
                const groupSymptoms = symptoms?.filter(s => s.group_id === group.id) || [];
                return (
                  <button
                    key={group.id}
                    onClick={() => {
                      setSelectedGroupId(group.id);
                      setSelectedSymptomId(null);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                      selectedGroupId === group.id
                        ? 'bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/50 dark:text-blue-300 dark:border-blue-800'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{group.name_no}</span>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {groupSymptoms.length} symptomer
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Symptoms Column */}
          <div className="lg:col-span-4 p-4">
            <h3 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Target className="h-5 w-5 text-purple-600" />
              Symptomer
            </h3>
            <div className="space-y-2 max-h-[800px] overflow-y-auto">
              {filteredSymptoms && filteredSymptoms.length > 0 ? (
                filteredSymptoms.map((symptom) => (
                  <button
                    key={symptom.id}
                    onClick={() => handleSymptomSelect(symptom.id)}
                    className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                      selectedSymptomId === symptom.id
                        ? 'bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-900/50 dark:text-purple-300 dark:border-purple-800'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{symptom.name_no}</span>
                      {symptom.risk_tag && (
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          symptom.risk_tag === 'suicide' ? 'bg-red-100 text-red-700' :
                          symptom.risk_tag === 'violence' ? 'bg-orange-100 text-orange-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {symptom.risk_tag === 'suicide' ? 'Selvmordsrisiko' :
                           symptom.risk_tag === 'violence' ? 'Voldsrisiko' :
                           symptom.risk_tag}
                        </span>
                      )}
                    </div>
                    {symptom.help_text && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                        {symptom.help_text}
                      </div>
                    )}
                  </button>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p className="text-sm">Velg en symptomgruppe</p>
                </div>
              )}
            </div>
          </div>

          {/* Approaches/Modules Column with Weighting */}
          <div className="lg:col-span-5 p-4">
            <h3 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Settings className="h-5 w-5 text-green-600" />
              Modulvektinger (0-100%)
            </h3>

            {selectedSymptom ? (
              <div className="space-y-3 max-h-[900px] overflow-y-auto">
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg mb-4">
                  <div className="text-sm font-medium text-blue-900 dark:text-blue-300">
                    {selectedSymptom.name_no}
                  </div>
                  {selectedSymptom.help_text && (
                    <div className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                      {selectedSymptom.help_text}
                    </div>
                  )}
                </div>

                {approaches && approaches.length > 0 ? (
                  approaches
                    .sort((a, b) => a.number - b.number)
                    .map((approach) => (
                      <div
                        key={approach.id}
                        className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-300 dark:hover:border-blue-700 transition-colors"
                      >
                        <div className="flex-shrink-0 w-20">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Modul {approach.number}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {approach.name_no}
                          </div>
                          {approach.description && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">
                              {approach.description}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={weights[approach.id] || 0}
                            onChange={(e) => handleWeightChange(approach.id, parseInt(e.target.value) || 0)}
                            disabled={!canEdit}
                            className="w-20 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center"
                          />
                          <span className="text-sm text-gray-500 dark:text-gray-400">%</span>
                        </div>
                      </div>
                    ))
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <p className="text-sm">Ingen moduler funnet</p>
                    <p className="text-xs mt-1">Opprett moduler først</p>
                  </div>
                )}

                {canEdit && approaches && approaches.length > 0 && (
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        0% = Ingen påvirkning. Høyere % = Sterkere anbefaling av modulen.
                      </div>
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
                      >
                        <Save className="h-4 w-4" />
                        {saving ? 'Lagrer...' : 'Lagre alle vektinger'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <Settings className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">Velg et symptom for å sette vektinger</p>
                <p className="text-xs mt-1">
                  Vektinger bestemmer hvor sterkt et symptom anbefaler hver modul
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Info box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900 dark:text-blue-300">
            <p className="font-medium mb-2">Slik fungerer vektinger:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Velg et symptom fra listen</li>
              <li>Sett vekting (0-100%) for hver modul</li>
              <li>0% = Symptomet har ingen påvirkning på denne modulen</li>
              <li>100% = Symptomet anbefaler sterkt denne modulen</li>
              <li>Lagre alle vektinger på én gang</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Statistics */}
      {selectedSymptom && approachWeights && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 dark:text-white mb-3">Statistikk for {selectedSymptom.name_no}</h4>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white dark:bg-gray-700 p-3 rounded-lg">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {approachWeights.filter(aw => aw.weight > 0).length}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Moduler med vekting</div>
            </div>
            <div className="bg-white dark:bg-gray-700 p-3 rounded-lg">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {Math.round(approachWeights.reduce((sum, aw) => sum + aw.weight, 0) / approachWeights.length)}%
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Gjennomsnittsvekting</div>
            </div>
            <div className="bg-white dark:bg-gray-700 p-3 rounded-lg">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {Math.max(...approachWeights.map(aw => aw.weight))}%
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Høyeste vekting</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
